import { getSupabaseClient } from '../../lib/supabase';
import { listTutorLearnerScopes } from '../insights/learnerInsightSupabase';
import { assertTutorSubjectScope } from '../insights/learnerInsightScope';
import {
  buildLearningSessionSummaries,
  type HistoryAnswerRow,
  type HistoryAssignmentItemRow,
  type HistoryAssignmentRow,
  type HistoryAttemptRow,
  type HistorySessionRow,
  type LearningSessionSummary,
} from './sessionHistory';

export type TutorHistoryCorrection = {
  correctionCount: number;
  previousResponse: unknown;
  correctedResponse: unknown;
  correctAfterCorrection: boolean | null;
};

export type TutorHistoryInk = {
  storageKey: string;
  strokeCount: number;
  pointCount: number;
  inputType: string;
  durationMs: number;
  captureReplay: boolean;
};

export type TutorHistoryAnswer = {
  answerId: string;
  sequence: number;
  learnerStatement: string;
  firstAttemptCorrect: boolean | null;
  finalCorrect: boolean | null;
  hintCount: number;
  activeDurationMs: number;
  answeredAt: string | null;
  correction: TutorHistoryCorrection | null;
  ink: TutorHistoryInk | null;
};

export type TutorHistorySession = LearningSessionSummary & {
  answers: TutorHistoryAnswer[];
};

export type TutorHistoryBundle = {
  sessions: TutorHistorySession[];
};

type EvidenceRow = {
  source_id: string | null;
  skill_id: string;
};

type SkillRow = {
  id: string;
  learner_statement: string | null;
  canonical_statement: string;
};

type CorrectionRow = {
  attempt_answer_id: string;
  correction_number: number;
  previous_response: unknown;
  corrected_response: unknown;
  correct_after_correction: boolean | null;
};

type InkRow = {
  attempt_answer_id: string;
  storage_key: string;
  stroke_count: number;
  point_count: number;
  input_type: string;
  duration_ms: number;
  capture_replay: boolean;
};

function localIsoDate(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function answerResultLabel(firstAttemptCorrect: boolean | null, finalCorrect: boolean | null) {
  if (firstAttemptCorrect === true) return 'Benar pada jawaban pertama';
  if (finalCorrect === true) return 'Benar setelah diperbaiki';
  return 'Perlu dilihat lagi';
}

export function tutorHistoryAnswerResult(answer: Pick<TutorHistoryAnswer, 'firstAttemptCorrect' | 'finalCorrect'>) {
  return answerResultLabel(answer.firstAttemptCorrect, answer.finalCorrect);
}

export async function loadTutorHistory(
  studentId: string,
  subjectId: string,
  onDate = localIsoDate(),
): Promise<TutorHistoryBundle> {
  const client = getSupabaseClient();
  if (!client) throw new Error('Supabase belum dikonfigurasi.');

  const scopes = await listTutorLearnerScopes(onDate);
  assertTutorSubjectScope(scopes, studentId, subjectId, onDate);

  const assignmentsResult = await client
    .from('assignments')
    .select('id,assigned_date,assignment_type,status,updated_at')
    .eq('student_id', studentId)
    .eq('subject_id', subjectId)
    .order('assigned_date', { ascending: false })
    .limit(40);
  if (assignmentsResult.error) throw assignmentsResult.error;
  const assignments = (assignmentsResult.data ?? []) as HistoryAssignmentRow[];
  const assignmentIds = assignments.map((row) => row.id);

  const sessionsResult = await client
    .from('learning_sessions')
    .select('id,assignment_id,started_at,ended_at,active_duration_ms,status')
    .eq('student_id', studentId)
    .eq('subject_id', subjectId)
    .order('started_at', { ascending: false })
    .limit(30);
  if (sessionsResult.error) throw sessionsResult.error;
  const sessions = (sessionsResult.data ?? []) as HistorySessionRow[];

  const assignmentItemsResult = assignmentIds.length
    ? await client
      .from('assignment_items')
      .select('id,assignment_id,sequence')
      .in('assignment_id', assignmentIds)
    : { data: [], error: null };
  if (assignmentItemsResult.error) throw assignmentItemsResult.error;
  const assignmentItems = (assignmentItemsResult.data ?? []) as HistoryAssignmentItemRow[];
  const assignmentItemIds = assignmentItems.map((row) => row.id);

  const attemptsResult = assignmentItemIds.length
    ? await client
      .from('attempts')
      .select('id,assignment_item_id,learning_session_id,status,started_at,submitted_at,active_duration_ms')
      .eq('student_id', studentId)
      .in('assignment_item_id', assignmentItemIds)
    : { data: [], error: null };
  if (attemptsResult.error) throw attemptsResult.error;
  const attempts = (attemptsResult.data ?? []) as HistoryAttemptRow[];
  const attemptIds = attempts.map((row) => row.id);

  const answersResult = attemptIds.length
    ? await client
      .from('attempt_answers')
      .select('id,attempt_id,first_attempt_correct,final_correct,hint_count,active_duration_ms,answered_at')
      .in('attempt_id', attemptIds)
    : { data: [], error: null };
  if (answersResult.error) throw answersResult.error;
  const answers = (answersResult.data ?? []) as HistoryAnswerRow[];
  const answerIds = answers.map((row) => row.id);

  const [evidenceResult, correctionResult, inkResult] = await Promise.all([
    answerIds.length
      ? client
        .from('skill_evidence_events')
        .select('source_id,skill_id')
        .eq('student_id', studentId)
        .in('source_id', answerIds)
      : Promise.resolve({ data: [], error: null }),
    answerIds.length
      ? client
        .from('corrections')
        .select('attempt_answer_id,correction_number,previous_response,corrected_response,correct_after_correction')
        .in('attempt_answer_id', answerIds)
        .order('correction_number', { ascending: true })
      : Promise.resolve({ data: [], error: null }),
    answerIds.length
      ? client
        .from('answer_ink_records')
        .select('attempt_answer_id,storage_key,stroke_count,point_count,input_type,duration_ms,capture_replay')
        .in('attempt_answer_id', answerIds)
      : Promise.resolve({ data: [], error: null }),
  ]);
  if (evidenceResult.error) throw evidenceResult.error;
  if (correctionResult.error) throw correctionResult.error;
  if (inkResult.error) throw inkResult.error;

  const evidence = (evidenceResult.data ?? []) as EvidenceRow[];
  const skillIds = [...new Set(evidence.map((row) => row.skill_id))];
  const skillsResult = skillIds.length
    ? await client
      .from('skills')
      .select('id,learner_statement,canonical_statement')
      .eq('subject_id', subjectId)
      .in('id', skillIds)
    : { data: [], error: null };
  if (skillsResult.error) throw skillsResult.error;

  const summaries = buildLearningSessionSummaries({
    assignments,
    assignmentItems,
    sessions,
    attempts,
    answers,
  });

  const itemSequence = new Map(assignmentItems.map((row) => [row.id, Number(row.sequence)]));
  const attemptMap = new Map(attempts.map((row) => [row.id, row]));
  const evidenceByAnswer = new Map(evidence.flatMap((row) => row.source_id ? [[row.source_id, row] as const] : []));
  const skillMap = new Map(((skillsResult.data ?? []) as SkillRow[]).map((row) => [row.id, row]));
  const correctionsByAnswer = new Map<string, CorrectionRow[]>();
  for (const row of (correctionResult.data ?? []) as CorrectionRow[]) {
    const rows = correctionsByAnswer.get(row.attempt_answer_id) ?? [];
    rows.push(row);
    correctionsByAnswer.set(row.attempt_answer_id, rows);
  }
  const inkByAnswer = new Map<string, InkRow>();
  for (const row of (inkResult.data ?? []) as InkRow[]) {
    if (!inkByAnswer.has(row.attempt_answer_id) || row.capture_replay) inkByAnswer.set(row.attempt_answer_id, row);
  }

  const answerDetailMap = new Map<string, TutorHistoryAnswer>();
  for (const answer of answers) {
    const attempt = attemptMap.get(answer.attempt_id);
    const evidenceRow = evidenceByAnswer.get(answer.id);
    const skill = evidenceRow ? skillMap.get(evidenceRow.skill_id) : undefined;
    const correctionRows = correctionsByAnswer.get(answer.id) ?? [];
    const firstCorrection = correctionRows[0];
    const lastCorrection = correctionRows[correctionRows.length - 1];
    const ink = inkByAnswer.get(answer.id);
    answerDetailMap.set(answer.id, {
      answerId: answer.id,
      sequence: attempt ? itemSequence.get(attempt.assignment_item_id) ?? 0 : 0,
      learnerStatement: skill?.learner_statement || skill?.canonical_statement || 'Latihan',
      firstAttemptCorrect: answer.first_attempt_correct,
      finalCorrect: answer.final_correct,
      hintCount: Number(answer.hint_count || 0),
      activeDurationMs: Number(answer.active_duration_ms || 0),
      answeredAt: answer.answered_at,
      correction: firstCorrection && lastCorrection ? {
        correctionCount: correctionRows.length,
        previousResponse: firstCorrection.previous_response,
        correctedResponse: lastCorrection.corrected_response,
        correctAfterCorrection: lastCorrection.correct_after_correction,
      } : null,
      ink: ink ? {
        storageKey: ink.storage_key,
        strokeCount: Number(ink.stroke_count || 0),
        pointCount: Number(ink.point_count || 0),
        inputType: ink.input_type,
        durationMs: Number(ink.duration_ms || 0),
        captureReplay: Boolean(ink.capture_replay),
      } : null,
    });
  }

  return {
    sessions: summaries.map((summary) => ({
      ...summary,
      answers: summary.answerIds
        .map((answerId) => answerDetailMap.get(answerId))
        .filter((row): row is TutorHistoryAnswer => Boolean(row))
        .sort((a, b) => a.sequence - b.sequence || String(a.answeredAt ?? '').localeCompare(String(b.answeredAt ?? ''))),
    })),
  };
}
